// Core data model shared by every contract standard.
// A ProjectBrief is the single source of truth the user fills in (or the AI
// drafts); each template turns it into a finished, combined contract document.

export type StandardId = "FIDIC_REDBOOK_2017" | "FIDIC_REDBOOK_1999" | "AIA_A101_2017" | "AIA_B101_2017";

/** True for standards that are professional design-services (Owner–Architect)
 * agreements rather than construction (Owner–Contractor) contracts. */
export function isDesignServices(s: StandardId): boolean {
  return s === "AIA_B101_2017";
}

export type PaperSize = "A4" | "LETTER";

export interface Party {
  /** Legal name as it should appear in the Agreement. */
  name: string;
  address: string;
  /** Person signing / authorised representative. */
  representative?: string;
  representativeTitle?: string;
  email?: string;
  phone?: string;
  /** Witness to this party's signature. */
  witnessName?: string;
  witnessAddress?: string;
  country?: string;
}

export interface DrawingRef {
  number: string;
  title: string;
  revision?: string;
  discipline?: string;
  date?: string;
}

export interface SpecialProvision {
  /** Sub-clause of the General Conditions being amended, e.g. "4.2". Optional. */
  clauseRef?: string;
  heading: string;
  text: string;
  kind?: "amend" | "add" | "delete" | "replace";
}

/** A free-text narrative block for the Special Conditions (Part B). */
export interface NarrativeItem {
  /** e.g. "A", "B" … or "1" — printed as the item marker. */
  marker?: string;
  heading: string;
  text: string;
}

export interface Money {
  amount: number;
  currency: string;
}

/** Kinds of communication issued to the Contractor, and their reference prefixes. */
export type CommsKind = "handover" | "ntp" | "programme" | "letter";
export const COMMS_META: Record<CommsKind, { prefix: string; label: string; clause?: string }> = {
  handover: { prefix: "SH", label: "Site Handover Letter", clause: "2.1" },
  ntp: { prefix: "NTP", label: "Notice to Proceed", clause: "8.1" },
  programme: { prefix: "PRG", label: "Request for Programme", clause: "8.3" },
  letter: { prefix: "LTR", label: "Letter to the Contractor" },
};

/** One entry in the register of communications issued to the Contractor. */
export interface CommsEntry {
  id: string;
  kind: CommsKind;
  /** Sequential reference as printed on the letter, e.g. "NTP-01". */
  ref: string;
  subject: string;
  /** Date the communication was issued (as printed). */
  date: string;
  issuedBy: string;
  issuedTo: string;
  /** Format(s) it was produced in, e.g. "Word", "PDF". */
  format?: string;
  /** Free-text note — e.g. acknowledgement received, response due. */
  note?: string;
  /** Id of the live share link for this letter, once one is created. Held on the
   * register entry (rather than loose state) so a re-shared letter keeps the same
   * link and the register shows what was actually sent to the Contractor. */
  shareId?: string;
}

export interface AdvanceInstallment {
  label: string;
  amount?: Money;
  percent?: number;
  /** Condition that triggers this disbursement, e.g. "on provision of the
   * Advance Payment Guarantee" or "on release of funds from the Employer's
   * financing bank, within 8 weeks of mobilisation". */
  trigger?: string;
}

export interface DaabConfig {
  /** Dispute-board mechanism. FIDIC 2017 default is DAAB; the Particular
   * Conditions may substitute a DRB (Dispute Resolution Board). */
  kind?: "DAAB" | "DRB";
  members: number; // 1 or 3
  appointmentWeeks?: number;
  registrationBody?: string; // e.g. "Guyana Association of Professional Engineers (GAPE)"
  chairperson?: string;
}

export interface ProjectBrief {
  standard: StandardId;

  // Identity
  projectName: string;
  projectLocation: string;
  contractReference?: string;
  /** The Atelier (studio cockpit) project this contract belongs to — set when the
   * project arrives from Atelier; certificates sync to its client portal. */
  atelierProjectId?: string;
  worksDescription: string;

  // Parties
  employer: Party; // AIA: Owner
  contractor: Party;
  engineer?: Party; // FIDIC Engineer / AIA Architect / Consultant

  // Commercial
  contractPrice: Money;
  contractPriceWords?: string;
  /** Whether the Contract Price / Accepted Contract Amount includes VAT. Defaults
   * to exclusive (the FIDIC norm — VAT added on certificates). */
  priceVat?: "exclusive" | "inclusive";
  /** Rate of exchange basis for payment (FIDIC Sub-Clause 14.15). "prevailing" =
   * the market rate ruling at the date of payment (fluctuates); "fixed" = a rate
   * fixed in the Contract for its duration. Defaults to prevailing. */
  exchangeRateBasis?: "prevailing" | "fixed";
  /** For "prevailing": the published source/reference for the rate (e.g. central bank). */
  exchangeRateSource?: string;
  /** For "fixed": the fixed rate(s), e.g. "USD 1 = GYD 209". */
  fixedExchangeRate?: string;
  /** Currency/currencies of payment for Contract Data 14.15, when the payment is
   * split across currencies (e.g. "50% USD / 50% GYD"). Falls back to the Contract
   * Price currency when unset. */
  paymentCurrencies?: string;

  // Architecture / design-services (Owner–Architect agreements, e.g. AIA B101)
  /** How the Architect's fee is set. Defaults to "stipulated" (contractPrice). */
  designFeeBasis?: "percentage" | "stipulated" | "hourly";
  /** For "percentage" basis: fee as a % of the Estimated Cost of the Work. */
  designFeePercent?: number;
  /** The Owner's budget / Estimated Cost of the Work the design serves. */
  estimatedConstructionCost?: Money;
  /** Which Basic Services phases are included (keys: SD, DD, CD, BN, CA).
   * Unset = all five. */
  architectServices?: string[];
  /** Reimbursable expenses note, e.g. "at cost plus 10%". */
  reimbursablesNote?: string;

  // Time
  /** Human string e.g. "30 Months" (FIDIC Contract Data is often expressed so). */
  timeForCompletion: string;
  commencementDate?: string;
  /** Defects Notification Period, human string e.g. "12 months". */
  defectsNotification: string;

  // Risk & money mechanics
  delayDamagesPerDay?: Money;
  delayDamagesCapPercent?: number;
  performanceSecurityPercent?: number;
  advancePaymentPercent?: number;
  /** Fixed cash amount of the first advance disbursement (overrides pro-rata). */
  advanceFixedStart?: Money;
  /** Number of advance disbursement installments. */
  advanceInstallments?: number;
  /** Explicit advance disbursement schedule — each installment with its trigger.
   * When present, drives a deterministic "Advance Payment" clause in Part B. */
  advanceSchedule?: AdvanceInstallment[];
  advanceRepayment?: string; // e.g. "4 installments starting from Valuation 2"
  retentionPercent?: number;
  retentionCapPercent?: number;
  retentionRelease?: string; // e.g. "5% at Practical Completion, 5% at end of DNP"
  minimumIpc?: Money;
  paymentPeriodDays?: number;
  /** Which payments-ledger contract this project's certificates are recorded under
   * (chosen in the IPC tab; remembered with the project). */
  ledgerTarget?: { projectId: string; contractId: string };
  overheadProfitPercent?: number; // 13.4 provisional sums

  // Legal
  governingLaw: string;
  rulingLanguage: string;
  communicationsLanguage?: string;
  disputeResolution: string;
  daab?: DaabConfig;

  // Narrative / Special Conditions (Part B)
  narrative: NarrativeItem[];
  specialProvisions: SpecialProvision[];

  // Document components
  drawings: DrawingRef[];
  specifications?: { title: string; reference?: string }[];
  /** Addenda incorporated, e.g. ["1","2","3"]. */
  addenda?: string[];

  // Master cover ("prepared by" block, matching the studio print cover)
  preparedBy?: { name?: string; org?: string; role?: string; address?: string };
  coverDate?: string;

  // Standalone Special Conditions of Contract document (12-clause style).
  specialConditionsTitle?: string;
  specialConditionsDate?: string;
  specialConditionsBlocks?: Block[];
  /** Assistant "add under the Special Conditions" items, folded into Section 5 at
   * render time as numbered clauses. Safety net so they render even if a client
   * stored them raw instead of appending to specialConditionsBlocks. */
  specialConditionsAdd?: { heading?: string; text: string }[];

  // Review / comment thread (party discussion toward sign-off)
  review?: ReviewComment[];
  /** Id of the shared (live) review on the backend, once created. */
  reviewId?: string;

  // Metadata
  agreementDate?: string;
  tenderAmountWords?: string;

  // Contract-administration letters (site handover / notice to proceed)
  /** Issue date printed on generated letters. Falls back to coverDate. */
  letterDate?: string;
  /** Effective date the Site is handed over / access given (Sub-Clause 2.1).
   * Falls back to commencementDate. */
  siteHandoverDate?: string;
  /** Extent of the Site handed over, e.g. "the whole of the Site". */
  siteHandoverExtent?: string;
  /** Date the Contractor's programme is required by (Sub-Clause 8.3). Falls back
   * to "within 28 days of the Commencement Date". */
  programmeDueDate?: string;
  /** Register of every communication issued to the Contractor — each letter/notice
   * generated is logged here with its sequential reference. */
  commsLog?: CommsEntry[];
  /** The executed contract as signed by the parties, scanned and uploaded. The
   * generated document is the draft; this is the one that was actually signed,
   * so it governs. Only the reference is stored — the bytes live in R2. */
  executedContract?: StoredFile;
  /** Date the contract was executed (printed alongside the upload). */
  executedDate?: string;
}

/** A file held in the R2 store. Mirrors StoredFile in client/filesApi.ts — declared
 * here so the shared contract/payment models don't depend on the client. */
export interface StoredFile {
  id: string;
  name: string;
  size: number;
  uploadedAt?: string;
}

export type ReviewParty = "Employer" | "Contractor" | "Engineer" | "Other";
export type ReviewStatus = "open" | "accepted" | "resolved";

export interface ReviewReply {
  id: string;
  party: ReviewParty;
  author?: string;
  text: string;
  at?: string;
}

export interface ReviewComment {
  id: string;
  /** Section id or clause the comment is anchored to. */
  anchor: string;
  anchorLabel: string;
  party: ReviewParty;
  author?: string;
  text: string;
  status: ReviewStatus;
  replies: ReviewReply[];
  at?: string;
}

/** A file the recipient uploaded against a shared document (e.g. an invoice
 * attached to a payment certificate via the share link). Held in the review's
 * Durable Object; the bytes live in R2 under `id`. */
export interface ReviewAttachment {
  id: string;
  name: string;
  size: number;
  /** ISO timestamp the file was attached. */
  at: string;
  /** Optional label the uploader gave it, e.g. an invoice reference. */
  note?: string;
}

/** The payee's confirmation, on the share link, that they received the certified
 * payment: their signature, name, date, and a photo of the receipt. Mirrors
 * CertReceipt in payments/types.ts — declared here for the shared review model. */
export interface ReviewReceipt {
  confirmedBy?: string;
  /** Signature drawn on the share link, as a PNG data URI. */
  signature?: string;
  confirmedDate?: string;
  /** Photo/scan of the receipt (R2 reference). */
  receiptPhoto?: ReviewAttachment;
  note?: string;
  at?: string;
}

// ---------------------------------------------------------------------------
// Rendered document model. Templates emit a list of ContractParts; each part
// gets a cover page, and the parts are combined into one file with a TOC.
// ---------------------------------------------------------------------------

export interface CoverSpec {
  /** Small label above the title, e.g. "SECTION 4" / "APPENDIX A". */
  kicker?: string;
  title: string;
  subtitle?: string;
  /** Bottom metadata block, label : value pairs. */
  meta?: { label: string; value: string }[];
  // Master "title-page" style fields (matching the studio's print cover).
  contractLabel?: string; // e.g. "CONSTRUCTION CONTRACT"
  standardLine?: string; // e.g. "FIDIC RED BOOK 2017"
  date?: string;
  forParty?: string; // "FOR …"
  preparedBy?: string[]; // "PREPARED BY …" stacked lines
  projectNo?: string;
}

export interface SignParty {
  /** Bare role — the renderer prints "Signed for and on behalf of the {role}",
   * so pass "Employer", not "For and on behalf of the Employer". */
  role: string; // "Employer" / "Contractor" / "Engineer" / "DAAB Member"
  /** Optional line printed above the signing line, e.g. "Acknowledged — receipt
   * of Notice". Use for a counter-signature that means something other than
   * simple execution. */
  caption?: string;
  name?: string;
  title?: string;
  witnessName?: string;
  witnessAddress?: string;
  /** Data-URI (PNG/JPEG) of an applied signature image — rendered on the
   * signature line when the party has digitally approved/signed. */
  signatureDataUri?: string;
  /** Date the signature was applied (printed on the Date line). */
  signedDate?: string;
}

export interface ListItem {
  /** Marker prefix, e.g. "1.", "•", "◦", "(a)". Empty string for no marker. */
  prefix?: string;
  text: string;
  /** Indent level, 0-based. */
  level?: number;
}

export type Block =
  | { type: "heading"; text: string; level: number }
  /** Body text. Justified by default (contract prose); set align "left" for lines
   * that must not be stretched across the measure — addresses, reference lines,
   * salutations. */
  | { type: "paragraph"; text: string; align?: "left" }
  | { type: "clause"; ref?: string; text: string } // numbered/lettered clause line
  | { type: "list"; items: ListItem[] }
  | { type: "table"; columns: string[]; rows: string[][] }
  | { type: "signblock"; parties: SignParty[] }
  | { type: "spacer" }
  | { type: "pagebreak" };

export interface ContractPart {
  id: string;
  /** Section name for the TOC and running header. */
  name: string;
  cover: CoverSpec;
  blocks: Block[];
}

export interface AssembledContract {
  title: string;
  standardLabel: string;
  masterCover: CoverSpec;
  parts: ContractPart[];
  notice: string;
  /** Skip the master title page — for short standalone documents (letters,
   * notices) that should print as the letter itself, not cover + letter. */
  noCover?: boolean;
}
